import { Container, Box } from 'components/box';
import { Text } from 'components/typography';
import { css } from '@emotion/react';
import DropdownMenu from 'components/_shared/widgets/pop-outs/dropdown-menu';
import { zIndices } from 'constants/z-indices';
import { useState } from 'react';
// NOTE: direct usage of the theme is discouraged in most circumstances.
// DO NOT emulate the following without understanding what you're doing.
import theme from 'themes/onedayonly';

const Category = ({
  category,
  isSelected,
  setSelectedCategory,
  containerRef,
}: {
  category: string;
  isSelected: boolean;
  setSelectedCategory: (category: string) => void;
  containerRef?: (category: string, node: HTMLDivElement | null) => void;
}) => (
  <Text
    ref={node => {
      containerRef ? containerRef(category, node) : undefined;
    }}
    display="inline-block"
    p="2"
    borderRadius="3"
    key={category}
    mr="10px"
    mb="10px"
    verticalAlign="top"
    fontWeight="600"
    css={theme => css`
      cursor: pointer;
      overflow: hidden;
      @media ${theme.mediaQueries.mobileOnly} {
        overflow: hidden;
      }
      ${isSelected &&
      css`
        font-weight: ${theme.fontWeights.bold};
        background-color: ${theme.colors.lightishGrey};
      `}
    `}
    onClick={() => setSelectedCategory(category)}
  >
    {category}
  </Text>
);

const CategoriesBar = ({
  categoriesList,
  selectedCategory,
  showMoreButton,
  setShowMoreButton,
  setSelectedCategory,
}: {
  categoriesList: string[];
  selectedCategory: string;
  showMoreButton: boolean;
  setShowMoreButton: (e: boolean) => void;
  setSelectedCategory: (category: string) => void;
}) => {
  // The width of the word Categories in the categories bar
  let categoriesWidth = 78;
  const { maxGridWidth } = theme;
  const [categoriesDropdownMenu, setCategoriesDropdownMenu] = useState<
    string[]
  >([]);

  const fakeCategory = (category: string, node: HTMLDivElement | null) => {
    if (node) {
      categoriesWidth += node.getBoundingClientRect().width;
      if (categoriesWidth > maxGridWidth) {
        const dropdownMenuItems: string[] = [...categoriesDropdownMenu];
        if (category) {
          dropdownMenuItems.push(category);
        }
        setCategoriesDropdownMenu(dropdownMenuItems);
        setShowMoreButton(true);
      }
    }
  };

  return (
    <>
      {/* Fake categories */}
      <Box
        backgroundColor="lightestGrey"
        position="fixed"
        width="100%"
        zIndex={zIndices.CATEGORIES_BAR}
        boxShadow="medium"
        mb="-200px"
        height="30px"
      >
        <Container className="show-for-desktop" py="2">
          <Box
            height="30px"
            overflowY="hidden"
            display="inline-block"
            css={css`
              white-space: break-spaces;
            `}
          >
            <Text
              fontWeight="bolder"
              pt="2"
              pb="2"
              display="inline-block"
              mr="2"
            >
              Categories:
            </Text>
            {categoriesList.map(category => (
              <Category
                key={category}
                category={category}
                isSelected={selectedCategory === category}
                setSelectedCategory={setSelectedCategory}
                containerRef={fakeCategory}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Desktop only */}
      <Box
        backgroundColor="lightestGrey"
        position="fixed"
        width="100%"
        zIndex={zIndices.CATEGORIES_BAR}
        boxShadow="medium"
      >
        <Container className="show-for-desktop" py="2">
          <Box
            marginTop="-1px"
            height="30px"
            overflowY="hidden"
            display="inline-block"
            css={css`
              white-space: break-spaces;
            `}
          >
            <Text
              fontWeight="bolder"
              pt="2"
              pb="2"
              display="inline-block"
              mr="2"
            >
              Categories:
            </Text>
            {categoriesList.map(category => (
              <Category
                key={category}
                category={category}
                isSelected={selectedCategory === category}
                setSelectedCategory={setSelectedCategory}
              />
            ))}
          </Box>
          {showMoreButton && categoriesDropdownMenu.length > 0 && (
            <Box display="inline-block" mt="8px" verticalAlign="top">
              <DropdownMenu
                value="More"
                color="darkGrey"
                flexProps={{ ml: 1 }}
                openedMenu="dropDownMenu"
                content={
                  <Box>
                    {categoriesDropdownMenu.map(category => (
                      <Text
                        className="open"
                        mx="3"
                        width="180px"
                        key={category}
                        bg="lightestGrey"
                        padding="3"
                        css={css`
                          cursor: pointer;
                        `}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </Text>
                    ))}
                  </Box>
                }
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* Not desktop */}
      <Box
        backgroundColor="lightestGrey"
        position="fixed"
        width="100%"
        zIndex={zIndices.CATEGORIES_BAR}
        boxShadow="medium"
        className="hide-for-desktop"
      >
        <Container
          py="2"
          css={css`
            white-space: nowrap;
          `}
          overflowX="scroll"
        >
          <Text fontWeight="bolder" pt="2" pb="2" display="inline-block" mr="2">
            Categories:
          </Text>
          {categoriesList.map(category => (
            <Category
              key={category}
              category={category}
              isSelected={selectedCategory === category}
              setSelectedCategory={setSelectedCategory}
            />
          ))}
        </Container>
      </Box>
    </>
  );
};

export default CategoriesBar;
